import { tracker } from '@lessonup/analytics';
import { useDocumentQuery, useLocale } from '@lessonup/client-integration';
import { logger } from '@lessonup/logging-classic';
import {
  BreadcrumbList,
  ManagedModal,
  ModalHeaderV1,
  NiceModal,
  SpaceBetween,
  spacing,
  styled,
  toast,
  useErrorContext,
  useModal,
} from '@lessonup/ui-components';
import { AppError, assertNever } from '@lessonup/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneratorInputType } from '../../../ChatGPTGenerator/MaiaGenerateLesson';
import { useConversionStatus } from '../../../SlideDeckImport/conversionStatus/useConversionStatus';
import { humandReadableFileSize } from '../../../Uploads/UploadsFeature.utils';
import { useUploadStatusPolling } from '../../../Uploads/hooks/useUploadStatusPolling';
import { useUploadsStatus } from '../../../Uploads/hooks/useUploadsStatus';
import { UploadRestrictionsDocument } from '../../Explorer.graphql.generated';
import { useUploadSlideDeckFile } from '../hooks/useUploadSlideDeckFile';
import { ChooseEditor } from './components/ChooseEditor';
import { ChooseNewEditorMode } from './components/ChooseNewEditorMode';
import { EditorSelection } from './components/EditorSelection';
import { GenerateLesson } from './components/GenerateLesson';
import { ImportLesson } from './components/ImportLesson';
import { MODAL_HEIGHT, MODAL_WIDTH, TRANSLATION_NAMESPACE } from './constants';
import { CreateLessonModalProps, EditorVersion, StepId } from './model';

type Step = {
  id: StepId;
  label: string;
  parentId?: StepId;
};

export type CreateLessonModalResponse =
  | { type: 'lesson'; version: EditorVersion; name: string }
  | { type: 'slideDeck'; name: string; lessonId: string; isWaitingOnConversion: boolean }
  | { type: 'maia'; generatorType: GeneratorInputType; version: EditorVersion };

const CreateLessonModal = NiceModal.create(({ lessonLocation, preferredStep }: CreateLessonModalProps) => {
  const modal = useModal();
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const locale = useLocale();
  const [currentStep, setCurrentStep] = useState<StepId>(preferredStep ?? 'selectEditor');
  const { setError: setToastError } = useErrorContext();
  const [invalidFileError, setInvalidFileError] = useState<string | undefined>();
  const [uploadInProgressId, setUploadInProgressId] = useState<string | undefined>();
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const { startPollingForIds, stopPolling } = useUploadStatusPolling({
    onError: (error) => {
      setToastError({
        error,
        customErrorDisplayMessage: {
          title: t('errors.conversionFailedTitle'),
          description: t('errors.genericPolling'),
        },
      });
      logger.error(error, { context: 'CreateNewLessonModal slide deck conversion status polling' });
      setIsConverting(false);
    },
  });
  const { data: uploadRestrictions } = useDocumentQuery(UploadRestrictionsDocument, {
    fetchPolicy: 'cache-first',
  });

  const { setConversionStatus, removeConversionStatus } = useConversionStatus();

  const [performUpload] = useUploadSlideDeckFile({
    onError: (error) => setToastError({ error }),
  });

  useUploadsStatus({
    ids: uploadInProgressId ? [uploadInProgressId] : [],
    onAllCompleted: (data) => {
      setUploadInProgressId(undefined);
      setIsConverting(false);
      if (!data) throw new AppError('unexpected-data', 'Expected data about completed uploads', { uploadInProgressId });
      if (data.viewer.uploadsByIds.length !== 1)
        throw new AppError('unexpected-data', 'Expected exactly one upload to be completed', {
          uploadIds: data.viewer.uploadsByIds.map(({ id }) => id),
        });

      const upload = data.viewer.uploadsByIds[0];

      if (upload.__typename !== 'SlideDeckUpload') {
        throw new AppError('unexpected-data', 'Expected status of a SlideDeckUpload', { uploadId: upload.id });
      }

      tracker.events.lessonPowerpointConverted({
        lessonId: upload.lessonId || '',
        pinCount: upload.slideCount || 0,
        fileSizeInBytes: upload.originalFile.sizeInBytes.toString(),
      });

      modal.resolve({
        type: 'slideDeck',
        name: upload.name,
        isWaitingOnConversion: true,
        lessonId: upload.lessonId,
      });
      modal.hide();
    },
    onSingleDocumentError() {
      setToastError({
        error: null,
        customErrorDisplayMessage: {
          title: t('errors.conversionFailedTitle'),
          description: t('errors.singleConversionFailed'),
        },
      });
      unsetConversionStatus();
    },
  });

  const onCreateLessonFromSlideDeck = async (name: string, file: File) => {
    setIsConverting(true);
    const isValid = assertSlideDeckValidity(file);

    if (!isValid) {
      setIsConverting(false);
      return;
    }

    const uploadId = await performUpload({
      file,
      lessonName: name,
      lessonLocation,
    });

    setUploadInProgressId(uploadId);

    if (!uploadId) {
      setIsConverting(false);
      return modal.reject(
        new AppError('unexpected-data', 'No upload ID, upload not created correctly', { name, file })
      );
    }

    startPollingForIds([uploadId]);

    setConversionStatus({ uploadId, status: 'UPLOADING', name, createdAt: new Date() });
    modal.resolve({ name, type: 'slideDeck', isWaitingOnConversion: true });
    modal.hide();
  };

  const createManualLesson = (name: string, version: EditorVersion) => {
    modal.resolve({ name, type: 'lesson', version });
    modal.hide();
  };

  /**
   *
   * @returns a boolean to indicate whether or not the file is valid.
   */
  const assertSlideDeckValidity = (file: File): boolean => {
    if (!uploadRestrictions) return true;
    const tooBig = file.size > uploadRestrictions.viewer.maxFileSizeInBytesForUploads;

    if (tooBig) {
      setInvalidFileError(
        t('errors.fileSizeTooBig', {
          maxFileSizeInMB: humandReadableFileSize(uploadRestrictions.viewer.maxFileSizeInBytesForUploads, locale),
        })
      );
      return false;
    }

    const fileTypeIsAllowed = uploadRestrictions?.viewer.acceptedMimeTypesForUploads.slideDeckTypes?.includes(
      file.type
    );

    if (!fileTypeIsAllowed) {
      setInvalidFileError(t('errors.fileTypeNotSupported'));
      return false;
    }

    setInvalidFileError(undefined);
    return true;
  };

  function onBeforeClose() {
    trackCancel(currentStep);
    if (!isConverting) {
      return;
    }
    toast({ type: 'neutral', message: t('closeDuringConversion') });
    unsetConversionStatus();
  }

  function unsetConversionStatus() {
    if (uploadInProgressId) removeConversionStatus({ uploadId: uploadInProgressId });
    setIsConverting(false);
    stopPolling();
  }

  const steps: Step[] = [
    { id: 'selectEditor', label: t('selectEditorTitle') },
    { id: 'classic', label: t('classicEditorTitle'), parentId: 'selectEditor' },
    { id: 'new', label: t('newEditorTitle'), parentId: 'selectEditor' },
    { id: 'lesson', label: t('lessonTitle'), parentId: 'new' },
    { id: 'sdi', label: t('sdiTitle'), parentId: 'new' },
    { id: 'ai', label: t('aiTitle'), parentId: 'new' },
  ];

  function trackSelectEditor(editor: 'classic' | 'new') {
    tracker.events.lessonCreateModalSelectVersion({
      editorVersion: editor === 'classic' ? 'v1' : 'v2',
    });
  }

  function trackSelectNewEditorMode(editor: 'ai' | 'lesson' | 'sdi') {
    const lessonCreationMethod = getLessonCreationMethodForEvent(editor);
    if (!lessonCreationMethod) return;

    tracker.events.lessonCreateModalSelectMethod({
      editorVersion: 'v2',
      lessonCreationMethod,
    });
  }

  function displayStep(step: StepId) {
    switch (step) {
      case 'selectEditor':
        return (
          <ChooseEditor
            onSelect={(version: 'classic' | 'new') => {
              trackSelectEditor(version);
              navigateToStep(version);
            }}
          />
        );
      case 'classic':
        return (
          <EditorSelection
            editorType="v1"
            createManualLesson={createManualLesson}
            back={() => navigateToStep('selectEditor')}
          />
        );
      case 'new':
        return (
          <ChooseNewEditorMode
            onSelect={(editor: 'ai' | 'lesson' | 'sdi') => {
              trackSelectNewEditorMode(editor);
              navigateToStep(editor);
            }}
            back={() => navigateToStep('selectEditor')}
          />
        );
      case 'lesson':
        return (
          <EditorSelection editorType="v2" createManualLesson={createManualLesson} back={() => navigateToStep('new')} />
        );
      case 'sdi':
        return (
          <ImportLesson
            modal={modal}
            onCreateLessonFromSlideDeck={onCreateLessonFromSlideDeck}
            invalidFileError={invalidFileError}
            loading={isConverting}
            onSelectFile={assertSlideDeckValidity}
            back={() => navigateToStep('new')}
          />
        );
      case 'ai':
        return <GenerateLesson modal={modal} editorType="v2" back={() => navigateToStep('new')} />;
      default:
        assertNever(step, 'Unknown step');
    }
  }

  function navigateToStep(step: StepId) {
    setCurrentStep(step);
  }

  function getBreadCrumbList(stepId: StepId, steps: Step[]): Step[] {
    const map = new Map(steps.map((step) => [step.id, step]));
    const initialStep = steps.find((x) => x.id === stepId);
    if (!initialStep) return [];

    const chain: Step[] = [initialStep];

    let current = initialStep;
    while (current?.parentId) {
      const parent = map.get(current.parentId);
      if (!parent) break;
      chain.unshift(parent);
      current = parent;
    }

    return chain;
  }

  function getBreadcrumbs() {
    return getBreadCrumbList(currentStep, steps).map((x) => ({ label: x.label, onClick: () => navigateToStep(x.id) }));
  }

  function cancel() {
    trackCancel(currentStep);
    modal.hide();
  }

  return (
    <ManagedModal
      modal={modal}
      contentLabel={t('title')}
      onBeforeClose={onBeforeClose}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
      maxHeight={MODAL_HEIGHT}
      overflowHidden
    >
      <StyledModalHeader
        type="breadcrumb"
        onCloseButtonClick={cancel}
        title={
          currentStep === 'selectEditor'
            ? undefined
            : {
                content: (
                  <BreadcrumbList
                    title={t('title')}
                    breadCrumbs={getBreadcrumbs()}
                    maxBreadcrumbWidth={500}
                    hideMiddleBreadcrumbsIfNeeded={false}
                  />
                ),
                size: 'S',
              }
        }
        showDivider={false}
        overflow="wrap"
      />
      <StyledSpaceBetween direction="y" spacing={spacing.size24}>
        {displayStep(currentStep)}
      </StyledSpaceBetween>
    </ManagedModal>
  );
});

export const showCreateLessonModal = (props: CreateLessonModalProps): Promise<CreateLessonModalResponse> => {
  tracker.events.lessonCreateModalStart({ modalVersion: null });
  return NiceModal.show(CreateLessonModal, props);
};

const StyledModalHeader = styled(ModalHeaderV1)`
  width: 100%;
`;

const StyledSpaceBetween = styled(SpaceBetween)`
  width: 100%;
  flex-grow: 1;
  height: ${MODAL_HEIGHT};
  min-height: calc(${MODAL_HEIGHT} - 60px);
  overflow-y: scroll;
  flex: 1;
  overflow: auto;
  margin-left: 0 !important; // Override initial SpaceBetween margin
`;

function trackCancel(currentStep?: StepId) {
  const editorVersion = currentStep === 'selectEditor' ? undefined : currentStep === 'classic' ? 'v1' : 'v2';
  tracker.events.lessonCreateModalCancel({
    editorVersion,
    lessonCreationMethod: getLessonCreationMethodForEvent(currentStep),
  });
}

function getLessonCreationMethodForEvent(currentStep?: StepId) {
  if (!currentStep) return null;

  switch (currentStep) {
    case 'selectEditor':
      return null;
    case 'sdi':
      return 'import';
    case 'ai':
      return 'ai';
    case 'new':
      return null;
    case 'lesson':
      return 'blank';
    case 'classic':
      return 'blank';
    default:
      return null;
  }
}
